<script setup lang="ts">
/**
 * GlobalSearch · 全局搜索（GS）
 * ------------------------------------------------------------
 * 原型对应：GS 模块 —— 顶栏搜索 + `/` 聚焦 + 前缀类型筛选。
 * 前缀：/域 /应用 /对象 /节点 /场景；裸词全类型。
 * 数据源：schema store（启动时已加载）+ sceneRepo 场景标题。
 * 结果点击 → 路由跳转（架构实体带 query code，架构页消费）。
 */
import { ref, computed, onMounted, onBeforeUnmount, nextTick, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useSchemaStore } from '@/domain/schema/store'
import { sceneRepo } from '@/data/api/shell-repository'
import { useToast } from '@/design/ui/UToast'
import { useI18n } from '@/design/i18n'

type Hit = {
  kind: 'dom' | 'app' | 'obj' | 'nd' | 'scene'
  kindLabel: string
  code: string
  name: string
  sub?: string
  to: string
  query?: Record<string, string>
}

const router = useRouter()
const schema = useSchemaStore()
const { t } = useI18n()

const open = ref(false)
const q = ref('')
const input = ref<HTMLInputElement | null>(null)
const scenes = ref<Record<string, any>>({})
const active = ref(0)

const PREFIXES: { p: string; kind: Hit['kind'] }[] = [
  { p: '/域', kind: 'dom' }, { p: '/应用', kind: 'app' },
  { p: '/对象', kind: 'obj' }, { p: '/节点', kind: 'nd' }, { p: '/场景', kind: 'scene' }
]
const KIND_LABEL: Record<Hit['kind'], string> = {
  dom: '应用域', app: '应用', obj: '数据对象', nd: '业务节点', scene: '场景'
}

onMounted(() => {
  window.addEventListener('keydown', onGlobalKey)
})
onBeforeUnmount(() => window.removeEventListener('keydown', onGlobalKey))

function onGlobalKey(e: KeyboardEvent) {
  const tag = (e.target as HTMLElement)?.tagName
  if (tag === 'INPUT' || tag === 'TEXTAREA') return
  if (e.key === '/') { e.preventDefault(); focusOpen() }
}
async function focusOpen() {
  open.value = true
  if (!schema.loaded) {
    try { await schema.load() }
    catch { useToast().show('架构搜索索引读取失败，请稍后重新打开搜索重试。') }
  }
  if (!scenes.value) scenes.value = await sceneRepo.scenes().catch(() => ({}))
  await nextTick()
  input.value?.focus()
}

const parsed = computed(() => {
  const raw = q.value.trim()
  const hit = PREFIXES.find(x => raw.startsWith(x.p))
  if (hit) return { kind: hit.kind as Hit['kind'] | null, word: raw.slice(hit.p.length).trim() }
  return { kind: null as Hit['kind'] | null, word: raw }
})

const hits = computed<Hit[]>(() => {
  const { kind, word } = parsed.value
  const w = word.toLowerCase()
  const match = (s: string) => !w || s.toLowerCase().includes(w)
  const out: Hit[] = []
  const want = (k: Hit['kind']) => !kind || kind === k
  if (want('dom'))
    schema.domains.filter(d => match(d.name) || match(d.code)).slice(0, 8)
      .forEach(d => out.push({ kind: 'dom', kindLabel: KIND_LABEL.dom, code: d.code, name: d.name, sub: d.code, to: '/arch/aa/apps', query: { dom: d.code } }))
  if (want('app'))
    schema.apps.filter(a => match(a.name) || match(a.code)).slice(0, 12)
      .forEach(a => out.push({ kind: 'app', kindLabel: KIND_LABEL.app, code: a.code, name: a.name, sub: `${a.dom} · ${a.grp}`, to: '/arch/aa/apps/' + encodeURIComponent(a.code) }))
  if (want('nd'))
    schema.nds.filter(n => match(n.name) || match(n.code)).slice(0, 8)
      .forEach(n => out.push({ kind: 'nd', kindLabel: KIND_LABEL.nd, code: n.code, name: n.name, sub: n.code, to: '/arch/ba/processes', query: { nd: n.code } }))
  if (want('scene'))
    Object.entries(scenes.value).filter(([k, s]) => match(String(s.title ?? k)) || match(k)).slice(0, 6)
      .forEach(([k, s]) => out.push({ kind: 'scene', kindLabel: KIND_LABEL.scene, code: k, name: String(s.title ?? k), sub: String(s.sub ?? ''), to: '/chat', query: { scene: k } }))
  return out.slice(0, 30)
})

watch([q, open], () => { active.value = 0 })

function pick(h: Hit) {
  open.value = false
  q.value = ''
  router.push({ path: h.to, query: h.query })
}
function onKey(e: KeyboardEvent) {
  if (e.key === 'Escape') { open.value = false; return }
  if (e.key === 'ArrowDown') { e.preventDefault(); active.value = Math.min(active.value + 1, hits.value.length - 1) }
  if (e.key === 'ArrowUp') { e.preventDefault(); active.value = Math.max(active.value - 1, 0) }
  if (e.key === 'Enter' && hits.value[active.value]) pick(hits.value[active.value])
}
</script>

<template>
  <div class="gs">
    <button class="gs-trigger" @click="focusOpen">
      <span class="gs-ic" aria-hidden="true">
        <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><circle cx="11" cy="11" r="7"/><path d="m20 20-4.2-4.2"/></svg>
      </span>
      <span class="gs-ph">{{ t.search.placeholder }}</span>
      <kbd>/</kbd>
    </button>
    <Teleport to="body">
      <div v-if="open" class="gs-veil" @click.self="open = false">
        <div class="gs-panel" @keydown="onKey">
          <div class="gs-head">
            <input ref="input" v-model="q" class="gs-input" :placeholder="t.search.prefixHint" />
            <button class="ubtn ghost" @click="open = false">Esc</button>
          </div>
          <div class="gs-hint">
            <code v-for="p in PREFIXES" :key="p.p">{{ p.p }}</code>
          </div>
          <div class="gs-body">
            <template v-if="hits.length">
              <button v-for="(h, i) in hits" :key="h.kind + h.code" class="gs-hit"
                      :class="{ on: i === active }" @mouseenter="active = i" @click="pick(h)">
                <span class="ubadge ai gs-kind">{{ h.kindLabel }}</span>
                <span class="gs-hit-name">{{ h.name }}</span>
                <span class="gs-hit-sub">{{ h.sub }}</span>
              </button>
            </template>
            <div v-else class="gs-empty">{{ t.common.empty }}</div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
.gs { flex: 1; max-width: 520px; }
.gs-trigger {
  display: flex; align-items: center; gap: var(--sp-2); width: 100%;
  height: 32px; padding: 0 var(--sp-3);
  border: 1px solid var(--line); border-radius: var(--r-s);
  background: var(--surface-2); color: var(--ink-4); font-size: var(--fs-sm);
}
.gs-trigger:hover { border-color: var(--accent-line); }
/* 放大镜与 `/` 快捷键统一为同规格键帽盒（22×20，同边框/底色/圆角/字号），视觉完全等大 */
.gs-ic, kbd {
  width: 22px; height: 20px; flex: none;
  display: grid; place-items: center;
  border: 1px solid var(--line); border-radius: 5px;
  background: var(--surface); font-size: var(--fs-xs); line-height: 1;
}
.gs-ic { padding: 0; }
.gs-ic svg { display: block; }
.gs-ph { flex: 1; text-align: left; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.gs-veil {
  position: fixed; inset: 0; z-index: 600;
  background: rgba(20,22,26,.35);
}
html[data-mode="dark"] .gs-veil { background: rgba(0,0,0,.55); }
.gs-panel {
  width: min(640px, calc(100vw - 32px)); margin: 12vh auto 0;
  background: var(--surface); border: 1px solid var(--line);
  border-radius: var(--r-l); box-shadow: var(--sh-3); overflow: hidden;
}
.gs-head { display: flex; gap: var(--sp-2); padding: var(--sp-5) var(--sp-7); border-bottom: 1px solid var(--line); }
.gs-input { flex: 1; border: none; outline: none; background: transparent; color: var(--ink); font-size: var(--fs-md); }
.gs-hint { display: flex; gap: var(--sp-2); padding: var(--sp-3) var(--sp-7); border-bottom: 1px solid var(--line-soft); }
.gs-hint code {
  padding: 1px 6px; border-radius: 5px; background: var(--surface-3);
  font-size: var(--fs-2xs); color: var(--ink-3); font-family: var(--mono);
}
.gs-body { max-height: 50vh; overflow: auto; padding: var(--sp-3); }
.gs-hit {
  display: flex; align-items: center; gap: var(--sp-3); width: 100%;
  padding: var(--sp-4) var(--sp-5); border-radius: var(--r-s); text-align: left;
}
.gs-hit.on { background: var(--hover); }
.gs-kind { flex: none; }
.gs-hit-name { font-size: var(--fs-sm); color: var(--ink); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.gs-hit-sub { margin-left: auto; flex: none; font-size: var(--fs-2xs); color: var(--ink-4); max-width: 40%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.gs-empty { padding: var(--sp-8); text-align: center; color: var(--ink-4); font-size: var(--fs-sm); }
</style>
